import { useState, useEffect } from 'react';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { showConfirm } from './ConfirmDialog';
import ProgramList from './ProgramList';
import ProgramDetail from './ProgramDetail';
import ProgramBlockDetail from './ProgramBlockDetail';

// ─── Orchestrator ─────────────────────────────────────────────────────────────
function Programs({ therapist, accentColor = KLINIO_DEFAULT_ACCENT, onNavigateToClient }) {
  const [programs, setPrograms] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [assignedClients, setAssignedClients] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [blockExercises, setBlockExercises] = useState([]);

  // Form-synlighet
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddBlockForm, setShowAddBlockForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);

  // Övningsväljare – sök + kategorifilter
  const [exSearch, setExSearch] = useState('');
  const [exCategory, setExCategory] = useState('');

  // Dosering – lägga till / redigera
  const [pendingExercise, setPendingExercise] = useState(null);
  const [editingPeId, setEditingPeId] = useState(null);

  // Duplicering
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const { data: programsData, error: progErr } = await supabase
        .from('programs')
        .select('*, program_blocks(end_week), client_programs(start_date, end_date)')
        .eq('therapist_id', therapist.id)
        .order('created_at', { ascending: false });
      if (progErr) throw progErr;
      setPrograms(programsData || []);

      const { data: exercisesData, error: exErr } = await supabase
        .from('exercises')
        .select('*')
        .eq('therapist_id', therapist.id)
        .order('title');
      if (exErr) throw exErr;
      setExercises(exercisesData || []);

      const { data: clientsData, error: clientErr } = await supabase
        .from('clients')
        .select('*')
        .eq('therapist_id', therapist.id)
        .order('name');
      if (clientErr) throw clientErr;
      setClients(clientsData || []);
    } catch (err) {
      showToast('Kunde inte ladda program. Försök igen.', 'error');
    }
  };

  const deleteProgram = (id) => {
    showConfirm({
      title: 'Ta bort program',
      description: 'Alla block och klienttilldelningar tas bort. Åtgärden kan inte ångras.',
      onConfirm: async () => {
        await supabase.from('programs').delete().eq('id', id);
        setSelectedProgram(null);
        setSelectedBlock(null);
        fetchAll();
      },
    });
  };

  const duplicateProgram = async (program) => {
    if (duplicating) return;
    setDuplicating(true);
    try {
      // 1. Skapa nytt program
      const { data: newProg, error: progErr } = await supabase
        .from('programs')
        .insert({
          therapist_id: therapist.id,
          title: program.title + ' (kopia)',
          description: program.description || null,
        })
        .select()
        .single();
      if (progErr) throw progErr;

      // 2. Hämta alla block i originalprogrammet
      const { data: origBlocks } = await supabase
        .from('program_blocks')
        .select('*')
        .eq('program_id', program.id)
        .order('sort_order');

      for (const block of (origBlocks || [])) {
        // 3. Kopiera blocket
        const { data: newBlock, error: blockErr } = await supabase
          .from('program_blocks')
          .insert({
            program_id: newProg.id,
            title: block.title,
            description: block.description || null,
            start_week: block.start_week,
            end_week: block.end_week,
            sort_order: block.sort_order,
          })
          .select()
          .single();
        if (blockErr) throw blockErr;

        // 4. Hämta övningar i blocket
        const { data: origPes } = await supabase
          .from('program_exercises')
          .select('*')
          .eq('block_id', block.id)
          .order('sort_order');

        if (origPes && origPes.length > 0) {
          const peInserts = origPes.map(pe => ({
            program_id: newProg.id,
            exercise_id: pe.exercise_id,
            block_id: newBlock.id,
            sort_order: pe.sort_order,
            sets: pe.sets || null,
            reps: pe.reps || null,
            duration_seconds: pe.duration_seconds || null,
            rest_seconds: pe.rest_seconds || null,
            notes: pe.notes || null,
          }));
          await supabase.from('program_exercises').insert(peInserts);
        }
      }

      // 5. Ladda om och öppna det nya programmet
      await fetchAll();
      const { data: refreshed } = await supabase
        .from('programs')
        .select('*, program_blocks(count)')
        .eq('id', newProg.id)
        .single();
      if (refreshed) selectProgram(refreshed);
    } catch (err) {
      showToast('Fel vid duplicering: ' + err.message, 'error');
    } finally {
      setDuplicating(false);
    }
  };

  const selectProgram = async (program) => {
    setSelectedProgram(program);
    setSelectedBlock(null);
    setShowAddBlockForm(false);
    setShowAssignForm(false);
    setPendingExercise(null);
    setEditingPeId(null);

    const { data: blocksData } = await supabase
      .from('program_blocks')
      .select('*, program_exercises(*, exercise:exercises(id, title))')
      .eq('program_id', program.id)
      .order('sort_order');
    setBlocks(blocksData || []);

    const { data: cpData } = await supabase
      .from('client_programs')
      .select('*, client:clients(*)')
      .eq('program_id', program.id);
    setAssignedClients(cpData || []);
  };

  const deleteBlock = (blockId) => {
    showConfirm({
      title: 'Ta bort block',
      description: 'Alla övningar i blocket tas bort. Åtgärden kan inte ångras.',
      onConfirm: async () => {
        await supabase.from('program_exercises').delete().eq('block_id', blockId);
        await supabase.from('program_blocks').delete().eq('id', blockId);
        setSelectedBlock(null);
        selectProgram(selectedProgram);
      },
    });
  };

  const selectBlock = async (block) => {
    setSelectedBlock(block);
    setExSearch('');
    setExCategory('');
    setPendingExercise(null);
    setEditingPeId(null);
    const { data: peData } = await supabase
      .from('program_exercises')
      .select('*, exercise:exercises(id, title)')
      .eq('block_id', block.id)
      .order('sort_order');
    setBlockExercises(peData || []);
  };

  // Uppdaterar övningslistan utan att nollställa söktillstånd,
  // och synkar övningarna direkt in i blocks-arrayen för ProgramDetail-vyn.
  const refreshBlockExercises = async () => {
    if (!selectedBlock) return;
    const { data: peData } = await supabase
      .from('program_exercises')
      .select('*, exercise:exercises(id, title)')
      .eq('block_id', selectedBlock.id)
      .order('sort_order');
    const list = peData || [];
    setBlockExercises(list);
    setBlocks(prev => prev.map(b =>
      b.id === selectedBlock.id
        ? { ...b, program_exercises: list }
        : b
    ));
  };

  // Tar bort en övning direkt från ProgramDetail-vyn (utan aktiv selectedBlock).
  const removeExerciseFromProgramDetail = async (peId) => {
    await supabase.from('program_exercises').delete().eq('id', peId);
    if (selectedProgram) selectProgram(selectedProgram);
  };

  const addExerciseToBlock = async (exerciseId, dose) => {
    const { error } = await supabase.from('program_exercises').insert({
      program_id: selectedProgram.id,
      exercise_id: exerciseId,
      block_id: selectedBlock.id,
      sort_order: blockExercises.length,
      sets: dose?.sets || null,
      reps: dose?.reps || null,
      duration_seconds: dose?.duration_seconds || null,
      rest_seconds: dose?.rest_seconds || null,
      notes: dose?.notes || null,
    });
    if (error) { showToast('Fel: ' + error.message, 'error'); return; }
    setPendingExercise(null);
    refreshBlockExercises();
  };

  const updateExerciseDose = async (peId, dose) => {
    const { error } = await supabase
      .from('program_exercises')
      .update({
        sets: dose?.sets || null,
        reps: dose?.reps || null,
        duration_seconds: dose?.duration_seconds || null,
        rest_seconds: dose?.rest_seconds || null,
        notes: dose?.notes || null,
      })
      .eq('id', peId);
    if (error) { showToast('Fel: ' + error.message, 'error'); return; }
    setEditingPeId(null);
    refreshBlockExercises();
  };

  const removeExerciseFromBlock = async (peId) => {
    await supabase.from('program_exercises').delete().eq('id', peId);
    refreshBlockExercises();
  };

  const moveExercise = async (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= blockExercises.length) return;
    const a = blockExercises[index];
    const b = blockExercises[swapIndex];
    await Promise.all([
      supabase.from('program_exercises').update({ sort_order: swapIndex }).eq('id', a.id),
      supabase.from('program_exercises').update({ sort_order: index }).eq('id', b.id),
    ]);
    refreshBlockExercises();
  };

  const unassignClient = (cpId) => {
    showConfirm({
      title: 'Ta bort tilldelning',
      description: 'Kunden tas bort från programmet.',
      onConfirm: async () => {
        await supabase.from('client_programs').delete().eq('id', cpId);
        selectProgram(selectedProgram);
      },
    });
  };

  const handleAssignClient = async ({ clientId, startDate, endDate, frequency }) => {
    const { data: inserted, error } = await supabase.from('client_programs').insert({
      client_id: clientId,
      program_id: selectedProgram.id,
      start_date: startDate,
      end_date: endDate || null,
      frequency_per_week: frequency,
      client_seen: false,
    }).select('id').single();
    if (error) { showToast('Fel: ' + error.message, 'error'); return; }
    setShowAssignForm(false);
    selectProgram(selectedProgram);
    if (inserted?.id) {
      supabase.functions.invoke('send-program-notification', {
        body: { client_program_id: inserted.id },
      }).catch(() => {}); // fire and forget
    }
  };

  // ─── Routing: visa rätt vy ────────────────────────────────────────────────

  if (selectedBlock) {
    return (
      <ProgramBlockDetail
        selectedBlock={selectedBlock}
        selectedProgram={selectedProgram}
        blockIndex={blocks.findIndex(b => b.id === selectedBlock?.id)}
        exercises={exercises}
        blockExercises={blockExercises}
        pendingExercise={pendingExercise}
        editingPeId={editingPeId}
        exSearch={exSearch}
        exCategory={exCategory}
        accentColor={accentColor}
        onBack={() => selectProgram(selectedProgram)}
        onSaveBlock={(updated) => { setSelectedBlock(updated); selectProgram(selectedProgram); }}
        onSetPendingExercise={setPendingExercise}
        onSetEditingPeId={setEditingPeId}
        onSetExSearch={setExSearch}
        onSetExCategory={setExCategory}
        onAddExercise={addExerciseToBlock}
        onUpdateExerciseDose={updateExerciseDose}
        onRemoveExercise={removeExerciseFromBlock}
        onMoveExercise={moveExercise}
      />
    );
  }

  if (selectedProgram) {
    return (
      <ProgramDetail
        key={selectedProgram.id}
        selectedProgram={selectedProgram}
        blocks={blocks}
        assignedClients={assignedClients}
        clients={clients}
        showAddBlockForm={showAddBlockForm}
        showAssignForm={showAssignForm}
        duplicating={duplicating}
        accentColor={accentColor}
        onBack={() => { setSelectedProgram(null); setBlocks([]); setShowAddBlockForm(false); setShowAssignForm(false); }}
        onSaveProgram={(updated) => {
          setSelectedProgram(updated);
          setPrograms(prev => prev.map(p =>
            p.id === updated.id
              ? { ...p, title: updated.title, description: updated.description }
              : p
          ));
        }}
        onShowAddBlockForm={() => setShowAddBlockForm(true)}
        onHideAddBlockForm={() => setShowAddBlockForm(false)}
        onBlockAdded={() => { setShowAddBlockForm(false); selectProgram(selectedProgram); }}
        onSelectBlock={selectBlock}
        onDeleteBlock={deleteBlock}
        onRemoveExercise={removeExerciseFromProgramDetail}
        onShowAssignForm={() => setShowAssignForm(true)}
        onHideAssignForm={() => setShowAssignForm(false)}
        onAssignClient={handleAssignClient}
        onUnassignClient={unassignClient}
        onDuplicateProgram={duplicateProgram}
        onDeleteProgram={deleteProgram}
        onNavigateToClient={onNavigateToClient}
      />
    );
  }

  return (
    <ProgramList
      programs={programs}
      duplicating={duplicating}
      showCreateForm={showCreateForm}
      therapistId={therapist.id}
      accentColor={accentColor}
      onSelectProgram={selectProgram}
      onDuplicateProgram={duplicateProgram}
      onDeleteProgram={deleteProgram}
      onShowCreateForm={() => setShowCreateForm(true)}
      onHideCreateForm={() => setShowCreateForm(false)}
      onProgramCreated={() => { setShowCreateForm(false); fetchAll(); }}
    />
  );
}

export default Programs;
